"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import { X } from "lucide-react";
import { getLenis } from "@/components/SmoothScrollProvider";
import useLockScroll from "@/hooks/useLockScroll";
import ActionButton from "@/components/Buttons/ActionButton";

interface ProductDetailModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    title: string;
    image: string;
    desc: string;
    specs: { label: string; value: string }[];
  } | null;
}

export default function ProductDetailModal({
  open,
  onClose,
  product,
}: ProductDetailModalProps) {
  // Lock scroll when modal is open (handles Lenis)
  useLockScroll(open);

  // Prevent background scrolling when open (standard fallback)
  useEffect(() => {
    if (!open) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [open]);

  if (!open || !product) return null;



  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop blur & overlay */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
      />

      {/* Modal Container */}
      <div
        data-lenis-prevent
        className="relative bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl z-10 max-h-[85vh] flex flex-col animate-popup-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <h3 className="text-base font-bold text-primary uppercase tracking-wide">
            Chi tiết sản phẩm: {product.title}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body (Scrollable) */}
        <div
          data-lenis-prevent
          className="p-6 overflow-y-auto space-y-6 flex-1"
        >
          {/* Product Image */}
          <div className="relative aspect-[16/9] w-full rounded-xl overflow-hidden shadow-md">
            <SmoothImage
              src={product.image}
              alt={product.title}
              fill
              priority
              quality={95}
              className="object-cover"
              sizes="(min-width: 768px) 1200px, 100vw"
            />
          </div>

          {/* Description */}
          <div className="text-xs text-gray-700 leading-relaxed bg-[#FBF9F5] p-4 rounded-xl border border-black/5">
            <span className="block font-bold text-primary uppercase tracking-wider text-[10px] mb-1">
              Mô tả loại hình
            </span>
            {product.desc}
          </div>

          {/* Specs Table */}
          <div className="space-y-1">
            <span className="block font-bold text-primary uppercase tracking-wider text-[10px] mb-3">
              Thông số chi tiết
            </span>
            <div className="border-t border-gray-100">
              {product.specs.map((spec, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row py-2.5 border-b border-dashed border-gray-200 text-xs sm:text-sm font-medium"
                >
                  <div className="w-full sm:w-1/3 text-[#6B5542] font-bold pb-0.5 sm:pb-0">
                    {spec.label}:
                  </div>
                  <div className="w-full sm:w-2/3 text-gray-800 leading-relaxed font-semibold">
                    {spec.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <ActionButton
            onClick={onClose}
            label="Đóng"
            className="px-8 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase tracking-wider transition duration-200"
          />
        </div>
      </div>
    </div>
  );
}
